import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import classNames from 'classnames';
import './CampaignStatsCompact.pcss';
import {
  CampaignSchedulerAttribute,
  CampaignStatuses,
  PlatformType,
} from '@mm/state/model/CampaignsModel';
import { CampaignTypeWithMappedPurposeAndDisplayStatus } from '@mm/state/selectors/campaignSelectors';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import Spinner from '../../../../../../common/components/Spinner';
import { Alert, Heading, Link } from '@mm/components/src';
import { formatPercentageWithSign } from '../../../../../../common/helpers/string-helpers';
import moment from 'moment';
import useRouter from 'use-react-router';
import {
  getDatesBack,
  getTitle,
  keys,
  StatsKey,
  StatsType,
} from '../stats-helpers';
import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { nextBillingTypeForFeatureSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { landmineNames } from '@mm/constants/landmine-names';

type Props = {
  className?: string;
  campaign: CampaignTypeWithMappedPurposeAndDisplayStatus;
  scheduler: CampaignSchedulerAttribute;
  showQueue?: () => void;
};

const format = formatPercentageWithSign('%');

const percent = (l: any, r: any, replacer = 'N/A') => {
  if (!l || !r) {
    return replacer || 0;
  }

  const res = l / r;

  if (!Number.isNaN(res)) {
    return format(res);
  }

  return replacer || 0;
};

export const getTotal = (
  key: StatsKey,
  platform?: PlatformType,
  scheduler?: CampaignSchedulerAttribute,
): number | string => {
  let totals = {};
  switch (platform) {
    case 'facebook':
      totals = {
        queued: scheduler?.currentQueuedCount || 0,
        delivered: scheduler?.deliveredCount || 0,
        unsubscribed: scheduler?.unsubscribedCount || 0,
        read: percent(scheduler?.readCount, scheduler?.deliveredCount),
        responded: percent(
          scheduler?.respondedCount,
          scheduler?.deliveredCount,
        ),
      };
      break;
    case 'sms':
      totals = {
        queued: scheduler?.smsCurrentQueuedCount || 0,
        delivered: scheduler?.smsDeliveredCount || 0,
        unsubscribed: '-',
        read: '-',
        responded: percent(
          scheduler?.smsRespondedCount,
          scheduler?.smsDeliveredCount,
        ),
      };
      break;
    case 'instagram':
      totals = {
        queued: scheduler?.instagramCurrentQueuedCount || 0,
        delivered: scheduler?.instagramDeliveredCount || 0,
        unsubscribed: scheduler?.instagramUnsubscribedCount || 0,
        read: percent(
          scheduler?.instagramReadCount,
          scheduler?.instagramDeliveredCount,
        ),
        responded: percent(
          scheduler?.instagramRespondedCount,
          scheduler?.instagramDeliveredCount,
        ),
      };
      break;
  }

  return totals[key];
};

const callbacks = {
  label: (
    item: { datasetIndex: number; value: number; label: string },
    { datasets }: { datasets: { label: string }[] },
  ) => {
    const base = `${item.label}: ${item.value}`;
    return '';
  },
};

const dates = getDatesBack(30);

const [startDate, endDate] = [dates[0], dates[dates.length - 1]].map(v =>
  v.format('YYYY-MM-DD'),
);

const options = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: 5,
  },
  legend: {
    display: false,
  },
  elements: {
    line: {
      tension: 0.7,
    },
    point: {
      radius: 2,
    },
  },
  tooltips: {
    mode: 'index',
    intersect: false,
    callbacks,
    enabled: true,
  },
  scales: {
    xAxes: [
      {
        ticks: {
          display: false,
          backdropPadding: 15,
        },
        gridLines: {
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
      },
    ],
    yAxes: [
      {
        ticks: {
          display: false,
          backdropPadding: 15,
        },
        beginAtZero: true,
        gridLines: {
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
      },
    ],
  },
};

const fetchStatusList: CampaignStatuses[] = [
  'queued',
  'sending',
  'paused',
  'delivered',
];

const last = (context, base, last) => {
  const index = context.dataIndex;
  return context.dataset?.data.length - 1 === index ? last : base;
};

export const CampaignStatsCompact = ({
  className,
  campaign,
  scheduler,
  showQueue,
}: Props) => {
  const campaignCRUDActions = useCampaignCRUDActions();
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const [state, setState] = useState<'loading' | 'error' | 'success' | ''>('');
  const [stats, setStats] = useState<StatsType>([]);

  const router = useRouter();

  const [width, setWidth] = useState<number>(0);

  const [platform] = campaign.platforms || [];
  const { status, statsArchived } = campaign;

  const ref = useRef<HTMLDivElement>(null);

  const getRangedData = useCallback(
    (key: StatsKey) => {
      return dates.map(v => {
        const stat = stats.find(({ date }) => v.isSame(moment(date)));
        return stat?.statuses?.[key]?.[platform] || 0;
      });
    },
    [dates, stats, platform],
  );

  const getChartData = useCallback(
    (key: StatsKey) => {
      const data = getRangedData(key);
      return {
        labels: dates.map(
          (v, i) => `${v.format('MM/DD/YYYY')}:${data[i]}`,
        ) as string[],
        datasets: [
          {
            data,
            fill: false,
            borderColor: context => last(context, '#3A50F3', '#6ABAA4'),
            pointBackgroundColor: context =>
              last(context, '#3A50F3', '#6ABAA4'),
            radius: context => last(context, 0, 3),
            hoverRadius: context => last(context, 0, 5),
          },
        ],
      };
    },
    [stats, getRangedData],
  );

  const getStats = async (campaignId: number) => {
    if (state !== 'loading' && campaignId && scheduler.id) {
      setState('loading');
      try {
        const res = await campaignCRUDActions.retrieveCampaignSchedulerStats(
          campaignId,
          scheduler.id,
          startDate,
          endDate,
        );
        setState('success');

        const stats = res.stats?.[scheduler.id];

        if (stats && Array.isArray(stats)) {
          setStats(stats);
        }
      } catch (err) {
        setState('error');
      }
    }
  };

  const showCharts = !statsArchived && fetchStatusList.includes(status);

  useEffect(() => {
    if (campaign?.id && showCharts) {
      getStats(campaign.id);
    }
  }, [campaign?.id]);

  useEffect(() => {
    if (ref?.current) {
      const styles = window.getComputedStyle(ref?.current);

      const [width, pl, pr] = [
        styles.width,
        styles.paddingLeft,
        styles.paddingRight,
      ].map(v => parseInt(v, 10));

      const contentW = width - pl - pr;
      if (contentW) {
        setWidth(contentW);
      }
    }
  }, [ref.current]);

  const billingType = useSelector((state: StoreState) =>
    nextBillingTypeForFeatureSelector(state, 'detailedDripStats'),
  );

  const onStatsClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (billingType) {
      e.preventDefault();
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['DETAILED-DRIP-STATS'],
        feature: 'detailedDripStats',
      });
    }
  };

  return (
    <div className={classNames(className, 'campaign-stats-compact')}>
      <div className="campaign-stats-compact__columns">
        {keys.map((key, i) => {
          const title = getTitle(key, platform);
          const total = getTotal(key, platform, scheduler);
          const chartData = getChartData(key);
          const empty = total === '-';

          if (empty) return <></>;

          const queuedCase = key === 'queued' && campaign && scheduler.id;

          return (
            <div
              key={key}
              className="campaign-stats-compact__column"
              ref={i === 0 ? ref : undefined}
            >
              <Heading size={4} className="campaign-stats-compact__heading">
                {title}

                {queuedCase && (
                  <i
                    className="fas fa-eye campaign-stats-compact__heading-icon"
                    onClick={showQueue}
                  />
                )}
              </Heading>
              <Heading
                size={3}
                className={classNames('campaign-stats-compact__value', {
                  'campaign-stats-compact__value--empty': empty,
                })}
              >
                {total}
              </Heading>
              {!!width && key !== 'queued' && showCharts && (
                <>
                  <span className="campaign-stats-compact__period">
                    Last 30 days:
                  </span>
                  <div className="campaign-stats-compact__chart">
                    {state === 'loading' && <Spinner standalone size={24} />}

                    {state === 'error' && (
                      <Alert type="warning" showCloseIcon={false}>
                        Couldn't load drip statistic, try again later
                      </Alert>
                    )}

                    {state === 'success' && (
                      <Line
                        data={chartData}
                        options={options}
                        width={width}
                        height={50}
                      />
                    )}
                  </div>
                </>
              )}

              {key !== 'queued' && showCharts && (
                <Link
                  className="campaign-stats-compact__link"
                  to={`${router.location.pathname}/stats/${scheduler.id}?key=${key}&platform=${platform}`}
                  onLinkClick={onStatsClick}
                >
                  View details
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
